// SPDX-License-Identifier: AGPL-3.0-or-later
import {makeAutoObservable} from 'mobx';

export interface RecognitionResult {
	isFinal: boolean;
	[index: number]: {transcript: string};
}
export interface RecognitionSession {
	lang: string;
	continuous: boolean;
	interimResults: boolean;
	onstart: (() => void) | null;
	onend: (() => void) | null;
	onerror: ((event: {error: string}) => void) | null;
	onresult: ((event: {results: ArrayLike<RecognitionResult>}) => void) | null;
	start(): void;
	stop(): void;
	abort(): void;
}
export type RecognitionConstructor = new () => RecognitionSession;
export function getRecognitionConstructor(): RecognitionConstructor | undefined {
	if (typeof window === 'undefined') return undefined;
	const browser = window as unknown as {
		SpeechRecognition?: RecognitionConstructor;
		webkitSpeechRecognition?: RecognitionConstructor;
	};
	return browser.SpeechRecognition ?? browser.webkitSpeechRecognition;
}

export class AssistantDictation {
	stage: 'idle' | 'starting' | 'listening' | 'stopping' | 'review' = 'idle';
	text = '';
	error: string | null = null;
	private session: RecognitionSession | null = null;
	private timer: ReturnType<typeof setTimeout> | null = null;

	constructor() {
		makeAutoObservable<this, 'session' | 'timer'>(this, {session: false, timer: false}, {autoBind: true});
	}

	get recording(): boolean {
		return this.stage === 'starting' || this.stage === 'listening' || this.stage === 'stopping';
	}

	start(language: string, Recognition = getRecognitionConstructor()): void {
		this.dispose();
		this.text = '';
		this.error = null;
		if (!Recognition) {
			this.fail('unsupported');
			return;
		}
		try {
			const session = new Recognition();
			this.session = session;
			this.stage = 'starting';
			session.lang = language;
			session.continuous = true;
			session.interimResults = true;
			session.onstart = () => {
				if (this.session === session) this.markListening();
			};
			session.onresult = (event) => {
				if (this.session !== session) return;
				this.setText(
					Array.from(event.results, (result) => result[0]?.transcript ?? '')
						.join(' ')
						.trim(),
				);
			};
			session.onerror = (event) => {
				if (this.session === session) this.fail(event.error);
			};
			session.onend = () => {
				if (this.session === session) this.finish();
			};
			this.timer = setTimeout(this.stop, 60_000);
			session.start();
		} catch {
			this.fail('audio-capture');
		}
	}

	private markListening(): void {
		if (this.stage === 'starting') this.stage = 'listening';
	}
	setText(value: string): void {
		this.text = value;
	}
	private clearTimer(): void {
		if (this.timer) clearTimeout(this.timer);
		this.timer = null;
	}
	private finish(): void {
		this.dispose();
		this.stage = 'review';
		if (!this.text && !this.error) this.error = 'no-speech';
	}
	private fail(error: string): void {
		this.error = error;
		this.dispose();
		this.stage = 'review';
	}
	stop(): void {
		if (!this.session || this.stage === 'stopping') return;
		this.stage = 'stopping';
		this.clearTimer();
		// Some implementations omit the end event after a permission/network failure.
		this.timer = setTimeout(this.finish, 3000);
		try {
			this.session.stop();
		} catch {
			this.finish();
		}
	}
	dispose(): void {
		this.clearTimer();
		const session = this.session;
		this.session = null;
		if (session) {
			session.onstart = null;
			session.onend = null;
			session.onerror = null;
			session.onresult = null;
			try {
				session.abort();
			} catch {}
		}
		this.stage = 'idle';
	}
}
